import { Component, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IconComponent } from '../icon/icon.component';

/**
 * Reusable dialog component with backdrop.
 * 
 * Provides a modal dialog with:
 * - Backdrop that closes dialog on click
 * - Header with title and close button
 * - Content projection for body and footer
 * - Keyboard escape support
 * 
 * @example
 * ```html
 * <app-dialog [open]="showDialog()" [title]="'Add Member'" (closed)="closeDialog()">
 *   <div dialog-body>
 *     <!-- Form content here -->
 *   </div>
 *   <div dialog-footer>
 *     <!-- Buttons here -->
 *   </div>
 * </app-dialog>
 * ```
 */
@Component({
  selector: 'app-dialog',
  standalone: true,
  imports: [CommonModule, IconComponent],
  templateUrl: './dialog.component.html',
  styleUrl: './dialog.component.scss',
  host: {
    '(document:keydown.escape)': 'onEscapeKey()'
  }
})
export class DialogComponent {
  /** Whether the dialog is open */
  readonly open = input.required<boolean>();
  
  /** Dialog title */
  readonly title = input<string>('');
  
  /** Maximum width of dialog (default: 500px) */
  readonly maxWidth = input<string>('500px');
  
  /** Emits when dialog should be closed */
  readonly closed = output<void>();

  protected onBackdropClick(): void {
    this.closed.emit();
  }

  protected onCloseClick(): void {
    this.closed.emit();
  }

  protected onEscapeKey(): void {
    if (this.open()) {
      this.closed.emit();
    }
  }
}
